import { ValidationResult } from '../types';
import { CHAR_COUNT_THRESHOLD } from '../constants';

/**
 * Validates and constrains user input for the chatbot widget.
 * Handles empty/whitespace checks, character limits, and rate limiting.
 */
export class InputValidator {
  private readonly maxLength: number;
  private readonly cooldownMs: number;
  private lastSubmissionTime: number = 0;

  constructor(maxLength: number, cooldownMs: number) {
    this.maxLength = maxLength;
    this.cooldownMs = cooldownMs;
  }

  /**
   * Validates the given input string.
   * Returns a ValidationResult indicating whether the input is valid,
   * and if not, the reason for rejection.
   */
  validate(input: string): ValidationResult {
    if (input.length === 0) {
      return { valid: false, reason: 'empty' };
    }

    if (input.trim().length === 0) {
      return { valid: false, reason: 'whitespace_only' };
    }

    if (!this.canSubmit()) {
      return { valid: false, reason: 'rate_limited' };
    }

    return { valid: true };
  }

  /**
   * Checks whether enough time has elapsed since the last submission
   * to allow a new submission.
   */
  canSubmit(): boolean {
    const now = Date.now();
    return now - this.lastSubmissionTime >= this.cooldownMs;
  }

  /**
   * Records the current timestamp as the last submission time.
   */
  recordSubmission(): void {
    this.lastSubmissionTime = Date.now();
  }

  /**
   * Returns the number of remaining characters allowed for the given input.
   */
  getRemainingChars(input: string): number {
    return this.maxLength - input.length;
  }

  /**
   * Returns true when the character counter should be displayed,
   * i.e., when the input length exceeds the threshold.
   */
  shouldShowCounter(input: string): boolean {
    return input.length > CHAR_COUNT_THRESHOLD;
  }
}
